import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle, ArrowLeft, Mail } from 'lucide-react';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (token) {
      verifyEmailWithToken(token);
    } else {
      setStatus('error');
      setMessage('No verification token provided. Please check your email for the verification link.');
    }
  }, [searchParams]);

  const verifyEmailWithToken = async (token: string) => {
    try {
      setStatus('verifying');
      const response = await apiService.post('/auth/verify-email', { token });

      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);

        setTimeout(() => {
          navigate('/onboarding');
        }, 2000);
      }

    } catch (error: any) {
      setStatus('error');

      if (error.response?.data?.error?.code === 'TOKEN_EXPIRED') {
        setMessage('Your verification link has expired. Please request a new one.');
      } else if (error.response?.data?.error?.code === 'INVALID_TOKEN') {
        setMessage('Invalid verification link. Please request a new one.');
      } else {
        setMessage(error.response?.data?.error?.message || 'Verification failed. Please try again.');
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }

    try {
      setResending(true);
      const response = await apiService.post('/auth/resend-verification', { email });

      setMessage(response.data.message || 'Verification email sent! Please check your inbox.');
      setStatus('success');

    } catch (error: any) {
      setMessage(error.response?.data?.error?.message || 'Failed to resend verification email');
      setStatus('error');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8">
          {/* Status Icon */}
          <div className="text-center mb-6">
            {status === 'verifying' && (
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
            )}

            {status === 'success' && (
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            )}

            {status === 'error' && (
              <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                <XCircle className="h-10 w-10 text-destructive" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center mb-2 text-foreground">
            {status === 'verifying' && 'Verifying Your Email'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h1>

          {/* Message */}
          <p className="text-center text-muted-foreground mb-6">
            {status === 'verifying' && 'Please wait while we verify your email address...'}
            {message}
          </p>

          {/* Success Actions */}
          {status === 'success' && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 text-center">
                  Redirecting you to onboarding...
                </p>
              </div>

              <Button asChild className="w-full">
                <Link to="/dashboard">
                  Go to Dashboard
                </Link>
              </Button>
            </div>
          )}

          {/* Error Actions - Resend Verification */}
          {status === 'error' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="pl-9"
                  />
                </div>
              </div>

              <Button
                onClick={handleResendVerification}
                disabled={resending}
                className="w-full"
              >
                {resending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </Button>

              <div className="text-center">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/login">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Login
                  </Link>
                </Button>
              </div>
            </div>
          )}

          {/* Already have an account */}
          {status === 'verifying' && (
            <div className="mt-6 text-center">
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </Link>
              </Button>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t">
            <p className="text-xs text-muted-foreground text-center">
              Didn't receive the email? Check your spam folder or request a new verification link.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
